import { useMemo } from 'react'
import { useAlertCountQuery } from '@/lib/nws/hooks'
import { useSpcQuery, useTropicsQuery, spcLevel } from '@/features/pulse/pulse-data'
import type { ThreatLevel } from '@/lib/criteria/levels'
import { levelRank } from '@/lib/criteria/levels'
import { AREA_CENTROIDS, CONUS_CENTROID, alertCountLevel, tropicalLevel } from './geo-data'

export type GlobePointKind = 'tropical' | 'spc' | 'alert'

export interface GlobePoint {
  id: string
  lat: number
  lon: number
  level: ThreatLevel
  kind: GlobePointKind
  /** Short title for the tap sheet. */
  label: string
  /** Secondary line (e.g. "Cat 3 · 120 mph"). */
  detail?: string
  /** In-app navigation target when tapped. */
  href: string
}

export interface GlobeData {
  points: GlobePoint[]
  isLoading: boolean
  /** Headline counts for the hero legend. */
  alertTotal: number | null
  tropicalCount: number | null
  spcCategory: string | null
}

/**
 * Composes the live feeds the app ALREADY fetches — nationwide NWS alert counts,
 * the SPC categorical outlook, and the global tropical feed — into geo-placed,
 * threat-coloured points for the dot globe. No new fetch paths.
 */
export function useGlobeData(): GlobeData {
  const counts = useAlertCountQuery()
  const spc = useSpcQuery()
  const tropics = useTropicsQuery()

  const points = useMemo<GlobePoint[]>(() => {
    const out: GlobePoint[] = []

    // Active tropical systems (global feed) — only those carrying a position.
    for (const s of tropics.data ?? []) {
      if (typeof s.lat !== 'number' || typeof s.lon !== 'number') continue
      const cat = s.category ? `Cat ${s.category}` : s.classification
      out.push({
        id: `tropical-${s.id}`,
        lat: s.lat,
        lon: s.lon,
        level: tropicalLevel(s.category, s.classification),
        kind: 'tropical',
        label: s.name,
        detail: [cat, s.basin, s.windMph ? `${Math.round(s.windMph)} mph` : null]
          .filter(Boolean)
          .join(' · '),
        href: '/pulse',
      })
    }

    // SPC convective outlook — one anchor over the contiguous US (no point geometry
    // in the feed), shown only when there's an actual severe risk.
    const spcCat = spc.data?.category
    if (spcCat && spcCat !== 'NONE' && spcCat !== 'TSTM') {
      out.push({
        id: 'spc-conus',
        lat: CONUS_CENTROID[0],
        lon: CONUS_CENTROID[1],
        level: spcLevel(spcCat),
        kind: 'spc',
        label: `Severe risk: ${spc.data?.label ?? spcCat}`,
        detail: 'SPC convective outlook',
        href: '/pulse',
      })
    }

    // Significant NWS alerts — per-area counts mapped to centroids.
    for (const [code, n] of Object.entries(counts.data?.areas ?? {})) {
      const c = AREA_CENTROIDS[code]
      if (!c || !n) continue
      out.push({
        id: `alert-${code}`,
        lat: c[0],
        lon: c[1],
        level: alertCountLevel(n),
        kind: 'alert',
        label: `${code} — ${n} active alert${n === 1 ? '' : 's'}`,
        detail: 'NWS active alerts',
        href: '/pulse',
      })
    }

    // Brightest (most intense) points draw last so they sit on top.
    return out.sort((a, b) => levelRank(a.level) - levelRank(b.level))
  }, [tropics.data, spc.data, counts.data])

  return {
    points,
    isLoading: counts.isLoading || spc.isLoading || tropics.isLoading,
    alertTotal: counts.data?.total ?? null,
    tropicalCount: tropics.data?.length ?? null,
    spcCategory: spc.data?.category ?? null,
  }
}
